import {defineStore} from 'pinia'
import {computed, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import Cookies from 'universal-cookie'
import type {QuizAlertType} from '@/components/Quiz/QuizAlert.vue'
import {type IVehicle, vehicleFactory} from '@/types/vehicle'
import {contactFactory, type IContact} from '@/types/contact'
import {addressFactory, type IAddress} from '@/types/address'
import {STEP_NAMES} from '@/helpers/constants'
import { inviteToCreditReport } from '@/services/quizService.ts'
import { captureException, captureMessage } from '@sentry/browser'

export const TOKEN_KEY = 'token'

export interface IAlert {
  type: QuizAlertType
  show: boolean
  message: string
}

export interface IAagLeadToken {
  pcp: string[]
  dpf: string[]
}

declare global {
  interface Window {
    WEBPAGE_SESSION_ID: string;
  }
}

export const useQuizStore = defineStore('quiz', () => {
  const cookies = new Cookies()
  const route = useRoute()
  const router = useRouter()
  const tokenRef = ref(route.query[TOKEN_KEY] || cookies.get(TOKEN_KEY) || '')
  const stepRef = ref(0)
  const stepsRef = ref(Number(STEP_NAMES.length))
  const emailRef = ref('')
  const vinRef = ref('')
  const vrmRef = ref('')
  const signatureRef = ref('')
  const vehiclesRef = ref<IVehicle[]>([])
  const addressesRef = ref<IAddress[]>([])
  const contactRef = ref<IContact>(contactFactory())
  const alertRef = ref<IAlert>({type: 'error', show: false, message: ''})
  const isFetchingRef = ref(false)
  const creditReportAuthenticationMessageRef = ref('User verification code sent via sms')
  const agreementsCountRef = ref(0)
  const aagLeadTokenRef = ref<IAagLeadToken>({pcp: [], dpf: []})
  const clientPortalUrlRef = ref('')
  const consentMarketingRef = ref(false)
  const leadSourceRef = ref('Website')
  const enrichmentAddressesRef = ref<IAddress[]>([])

  const step = computed(() => stepRef)
  const steps = computed(() => stepsRef)
  const email = computed(() => emailRef)
  const token = computed(() => tokenRef)
  const vin = computed(() => vinRef)
  const vrm = computed(() => vrmRef)
  const signature = computed(() => signatureRef)
  const contact = computed(() => contactRef.value)
  const vehicles = computed(() => vehiclesRef)
  const addresses = computed(() => addressesRef)
  const alert = computed(() => alertRef)
  const isFetching = computed(() => isFetchingRef)
  const stepRouteParam = computed(() => (route.params.step ? Number(route.params.step) : 1))
  const selectedVehicles = computed(() => vehiclesRef.value.filter((vehicle) => vehicle.selected))
  const stepName = computed(() => STEP_NAMES[stepRef.value])
  const creditReportAuthenticationMessage = computed(() => creditReportAuthenticationMessageRef)
  const agreementsCount = computed(() => agreementsCountRef)
  const aagLeadToken = computed(() => aagLeadTokenRef)
  const newerSelectedVehicle = computed(() => selectedVehicles.value ? selectedVehicles.value.sort(byRegistrationDateDesc)[0] : null)
  const clientPortalUrl = computed(() => clientPortalUrlRef)
  const leadSource = computed(() => leadSourceRef)
  const consentMarketing = computed(() => consentMarketingRef)
  const enrichmentAddresses = computed(() => enrichmentAddressesRef)

  function byRegistrationDateDesc(firstVehicle: IVehicle, secondVehicle: IVehicle): number {
    const firstRegistrationDate = new Date(firstVehicle.first_registered);
    const secondRegistrationDate = new Date(secondVehicle.first_registered);
    return secondRegistrationDate.getTime() - firstRegistrationDate.getTime();
  }

  function goToStepPath(stepPath: number) {
    if (stepPath < 1 || stepPath > stepsRef.value) return
    closeAlert();
    return router.push({path: `/quiz/${stepPath}`, query: route.query})
  }

  function goToNextStep() {
    return goToStepPath(stepRouteParam.value + 1)
  }

  function goToPreviousStep() {
    return goToStepPath(stepRouteParam.value - 1)
  }

  function setEmail(value: string) {
    emailRef.value = value
  }

  function setToken(value: string) {
    cookies.remove(TOKEN_KEY)
    const now = new Date()
    const oneDayInMilliseconds = 24 * 60 * 60 * 1000
    const expires = new Date(now.getTime() + oneDayInMilliseconds)
    cookies.set('token', value, {expires, path: '/'})
    tokenRef.value = value
  }

  function removeToken() {
    cookies.remove(TOKEN_KEY)
    tokenRef.value = ''
  }

  function setVin(value: string) {
    vinRef.value = value
  }

  function setVrm(value: string) {
    vrmRef.value = value
  }

  function setSignature(value: string) {
    signatureRef.value = value
  }

  function showAlert(message: string, type: QuizAlertType = 'error') {
    alertRef.value = {type, show: true, message}
  }

  function closeAlert() {
    alertRef.value.show = false
  }

  function setIsFetching(value: boolean) {
    isFetchingRef.value = value
  }

  function setLeadSource(value: string) {
    leadSourceRef.value = value;
  }

  function setConsentMarketing(value: boolean) {
    consentMarketingRef.value = value;
  }

  function clearStore() {
    emailRef.value = ''
    tokenRef.value = ''
    vinRef.value = ''
    vrmRef.value = ''
    consentMarketingRef.value = false
    signatureRef.value = ''
    leadSourceRef.value = ''
    aagLeadTokenRef.value = {
      pcp: [],
      dpf: []
    }
    vehiclesRef.value = []
    addressesRef.value = []
    contactRef.value = contactFactory()
    cookies.remove(TOKEN_KEY, {path: '/'})
    console.log('Clearing store');
    try {
      sessionStorage.removeItem("WEBPAGE_SESSION_ID");
    } catch (e) {
      console.log('Session Storage is disabled');
    } finally {
      window.WEBPAGE_SESSION_ID = '';
    }
  }

  function resetQuiz(redirect = true) {
    clearStore()
    stepRef.value = 0
    if (redirect) {
      return router.push({path: '/quiz/1', query: route.query})
    }
    return Promise.resolve();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function restoreData(payload: any) {
    if (payload.email) setEmail(payload.email)
    if (payload.vin) setVin(payload.vin || '')
    if (payload.vrm) setVrm(payload.vrm || '')
    if (payload.signature) setSignature(payload.signature || '')
    if (payload?.campaign?.lead_source_name) setLeadSource(payload.campaign.lead_source_name)
    if (payload.vehicles) {
      const vehicles = payload.vehicles.filter((v: IVehicle) => v.dpf_qualified)
      setVehicles(vehicles.map(vehicleFactory))
    }
    if (payload.addresses) setAddresses((payload.addresses || []).map(addressFactory))
    if (payload.contact) setContact(contactFactory(payload.contact))
    if (payload.consent_marketing) setConsentMarketing(payload.consent_marketing || false)
    if (payload?.campaign?.lead_source_name) setLeadSource(payload?.campaign?.lead_source_name)
    if (payload?.aag_lead_token) setAagLeadToken(payload.aag_lead_token)
  }

  function setVehicles(value: IVehicle[]) {
    vehiclesRef.value = value
  }

  function setAddresses(value: IAddress[]) {
    addressesRef.value = value
  }

  function setEnrichmentAddresses(value: IAddress[]) {
    enrichmentAddressesRef.value = value
  }

  function setContact(value: IContact) {
    contactRef.value = value
  }

  function setCreditReportAuthenticationMessage(message: string) {
    creditReportAuthenticationMessageRef.value = message
  }

  function setAgreementsCount(value: number) {
    agreementsCountRef.value = value
  }

  function setAagLeadToken(value: IAagLeadToken) {
    aagLeadTokenRef.value.pcp = value.pcp;
    aagLeadTokenRef.value.dpf = value.dpf;
  }

  function setClientPortalUrl(value: string) {
    clientPortalUrlRef.value = value;
  }

  async function handleAddressLookup() {
    try {
      setIsFetching(true)
      // const response = await addressLookup(tokenRef.value)
      // enrichmentAddressesRef.value = response
      //
      // if (response.length) {
      //   return router.push({path: '/quiz/8', query: route.query})
      // }

      const payload  = {
        token: tokenRef.value,
        addresses: [],
      }
      const sendInviteResponse = await inviteToCreditReport(payload)
      creditReportAuthenticationMessageRef.value = sendInviteResponse.message

      return router.push({path: '/quiz/9', query: route.query})
    } catch (err: any) {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on addressLookup: SignaturePad.vue");
        captureException(err);
      }
    } finally {
      setIsFetching(false)
    }
  }

  async function continueQuiz() {
    let continueFrom = 1
    if (emailRef.value) continueFrom = 3

    const hasVehicles = vehiclesRef.value.length > 0

    const selectedVehicles = vehiclesRef.value.filter((vehicle) => vehicle.selected)
    const hasSelectedVehicles = selectedVehicles.length > 0

    if (hasVehicles && hasSelectedVehicles) continueFrom = 4

    const allSelectedVehiclesHasSelectedDate = hasSelectedVehicles && selectedVehicles.every(i => !!i.selected_date)
    if (!hasVehicles || allSelectedVehiclesHasSelectedDate) continueFrom = 5

    if (addressesRef.value.length) continueFrom = 6

    if (
      contactRef.value.title &&
      contactRef.value.first_name &&
      contactRef.value.last_name &&
      contactRef.value.phone
    )
      continueFrom = 7

    if (contactRef.value.dob && signatureRef.value) continueFrom = 8

    if (continueFrom === 8) {
      return handleAddressLookup()
    }

    return router.push({path: `/quiz/${continueFrom}`, query: route.query})
  }

  function getStoredData() {
    return {
      step: stepName.value,
      token: tokenRef.value,
      aag_lead_token: aagLeadTokenRef.value,
      consent_marketing: consentMarketingRef.value,
      email: emailRef.value,
      vin: vinRef.value,
      vrm: vrmRef.value,
      signature: signatureRef.value,
      contact: {...contactRef.value},
      vehicles: vehiclesRef.value.map((vehicle) => ({...vehicle})),
      addresses: addressesRef.value.map((address) => ({...address})),
    }
  }

  watch(
    () => route.params.step,
    (newStep) => {
      stepRef.value = newStep ? Number(newStep) - 1 : 0
    },
    {immediate: true},
  )

  return {
    step,
    steps,
    email,
    token,
    vin,
    vrm,
    contact,
    vehicles,
    consentMarketing,
    selectedVehicles,
    addresses,
    signature,
    alert,
    isFetching,
    stepRouteParam,
    stepName,
    aagLeadToken,
    creditReportAuthenticationMessage,
    newerSelectedVehicle,
    agreementsCount,
    clientPortalUrl,
    leadSource,
    enrichmentAddresses,
    handleAddressLookup,
    setLeadSource,
    setConsentMarketing,
    setClientPortalUrl,
    clearStore,
    getStoredData,
    showAlert,
    closeAlert,
    setIsFetching,
    resetQuiz,
    setSignature,
    setEmail,
    setToken,
    removeToken,
    setVin,
    setVrm,
    setVehicles,
    setAddresses,
    restoreData,
    setContact,
    goToStepPath,
    goToNextStep,
    goToPreviousStep,
    setAagLeadToken,
    continueQuiz,
    setCreditReportAuthenticationMessage,
    setAgreementsCount,
  }
})
